import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, Send, X, Loader, Sparkles, User, Bot, Minimize2, Maximize2 } from 'lucide-react';
import { UserProfile } from '../types';
import { aiService } from '../services/aiService';
import { trackAIChatMessage } from '../services/analyticsService';
import { recordProductEvent } from '../services/productEvents';
import { renderMarkdownToHTML } from '../utils/formatAIText';
import { CRISIS_HELP_REPLY, userTextRequestsCrisisHelp } from '../utils/aiSafety';

interface AskClubIQProps {
  userProfile: UserProfile | null;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const SUGGESTED_QUESTIONS = [
  "What clubs should I add to my bag?",
  "How can I improve my distance?",
  "What's the best driver for my swing speed?",
  "Should I get a 3-wood or a 5-wood?",
  "How do I know if I need new irons?",
  "What wedge gapping should I use?"
];

const AskClubIQ: React.FC<AskClubIQProps> = ({ userProfile }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  const generateSystemContext = () => {
    if (!userProfile) {
      return `You are ClubIQ, a golf assistant chatbot. The user hasn't signed in yet.

RULES:
- Answer the question directly in 1-2 sentences FIRST
- Only add brief context after if truly needed
- Never pad responses with filler or flattery
- You are a chat assistant only — you CANNOT modify the user's bag, add clubs, remove clubs, or change any settings. If asked, tell them to use the app's bag editor.
- If the user expresses self-harm or suicidal thoughts, do not give golf advice. Tell them to call or text 988 in the United States.`;
    }

    const clubs = userProfile.golfBag?.map(c => `${c.type}: ${c.brand || ''} ${c.model || ''} (${c.distance || '?'} yds)`).join(', ') || 'No clubs added';
    
    return `You are ClubIQ, a golf assistant chatbot for ${userProfile.firstName || 'a golfer'}.

PROFILE: Handicap ${userProfile.handicap ?? '?'} | ${userProfile.skillLevel || '?'} | Age ${userProfile.age || '?'} | Bag: ${clubs}

RULES — follow these strictly:
1. Answer the question DIRECTLY in 1-3 sentences first. Lead with the answer, not preamble.
2. If more context would help, add a brief follow-up (2-3 sentences max). No more.
3. NEVER pad with filler, flattery, or generic encouragement like "That's exciting!" or "Great choice!"
4. NEVER start with "Hey [name]!" or greetings. Just answer.
5. Be specific — use numbers, club names, and distances from their profile when relevant.
6. You are a chat assistant only — you CANNOT modify the user's bag, add clubs, remove clubs, swap clubs, or change distances. If asked to make changes, tell them clearly: "I can't make changes to your bag directly. Use the edit button next to any club in your bag to make changes."
7. Keep total response under 100 words unless the question genuinely requires more detail.
8. Don't repeat information the user already knows (like what's in their bag).
9. If the user expresses self-harm or suicidal thoughts, do not give golf advice. Tell them to call or text 988 in the United States.`;
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    trackAIChatMessage('question');
    void recordProductEvent('AI_CHAT_USED');

    if (userTextRequestsCrisisHelp(userMessage.content)) {
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: CRISIS_HELP_REPLY,
        timestamp: new Date(),
      }]);
      setIsLoading(false);
      return;
    }

    try {
      // Build conversation history for context
      const conversationHistory = messages.slice(-10).map(m => ({
        role: m.role as 'user' | 'assistant',
        content: m.content
      }));

      conversationHistory.push({
        role: 'user',
        content: userMessage.content
      });

      const response = await aiService.getChatResponse(
        userMessage.content,
        generateSystemContext(),
        conversationHistory
      );

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
      trackAIChatMessage('response');
    } catch (error: any) {
      console.error('Chat error:', error);
      // Show actual error for debugging - will help identify the issue
      const errorText = error?.message || "Unknown error";
      
      // Check if it's the API key error specifically
      let userFriendlyMessage = "";
      if (errorText.includes("OPENAI_API_KEY")) {
        userFriendlyMessage = `API KEY NOT CONFIGURED: ${errorText}. The OPENAI_API_KEY environment variable needs to be set in Vercel Dashboard → Settings → Environment Variables.`;
      } else if (errorText.includes("401") || errorText.includes("Unauthorized")) {
        userFriendlyMessage = `INVALID API KEY: The OpenAI API key appears to be invalid or expired. Please check Vercel environment variables.`;
      } else if (errorText.includes("429")) {
        userFriendlyMessage = `RATE LIMITED: Too many requests. Please wait a moment and try again.`;
      } else if (errorText.includes("500")) {
        userFriendlyMessage = `SERVER ERROR: ${errorText}. Check Vercel function logs for details.`;
      } else {
        userFriendlyMessage = `CONNECTION ERROR: ${errorText}`;
      }
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: userFriendlyMessage,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (question: string) => {
    setInputValue(question);
    inputRef.current?.focus();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const fabPositionClass =
    "fixed z-50 bottom-[max(1.25rem,env(safe-area-inset-bottom,0px)+0.5rem)] right-[max(1.25rem,env(safe-area-inset-right,0px))]";

  // Floating button when closed
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={`${fabPositionClass} w-14 h-14 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center text-white group`}
        title="Ask ClubIQ"
      >
        <MessageCircle className="w-6 h-6" />
        <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold animate-pulse">
          AI
        </span>
      </button>
    );
  }

  // Minimized state
  if (isMinimized) {
    return (
      <div className={`${fabPositionClass}`}>
        <button
          onClick={() => setIsMinimized(false)}
          className="flex items-center gap-3 bg-white rounded-full shadow-lg pl-4 pr-2 py-2 hover:shadow-xl transition-all"
        >
          <Sparkles className="w-5 h-5 text-green-600" />
          <span className="font-medium text-gray-900">Ask ClubIQ</span>
          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
            <Maximize2 className="w-4 h-4 text-gray-600" />
          </div>
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/40 z-40"
        onClick={() => setIsOpen(false)}
      />
      
      {/* Chat Container - Compact modal on mobile, floating on desktop */}
      <div className="fixed left-4 right-4 bottom-[max(1rem,env(safe-area-inset-bottom,0px)+0.5rem)] md:left-auto md:bottom-6 md:right-6 md:w-96 bg-white rounded-2xl shadow-2xl z-50 flex flex-col max-h-[70vh] md:max-h-[500px] overflow-hidden border border-gray-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-4 py-2.5 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-white text-sm">Ask ClubIQ</h3>
              <p className="text-[10px] text-green-100">AI Golf Assistant</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMinimized(true)}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors hidden md:block"
              title="Minimize"
            >
              <Minimize2 className="w-4 h-4 text-white" />
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              title="Close"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-[200px]">
          {messages.length === 0 ? (
            <div className="text-center py-4">
              <Sparkles className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <h4 className="font-medium text-gray-900 mb-1 text-sm">How can I help?</h4>
              <p className="text-xs text-gray-500 mb-4">
                Ask about equipment, your bag, or tips.
              </p>
              <div className="space-y-2">
                {SUGGESTED_QUESTIONS.slice(0, 3).map((question, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSuggestionClick(question)}
                    className="block w-full text-left text-xs px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-gray-700 transition-colors border border-gray-200"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={`flex gap-2 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
              >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                  message.role === 'user' 
                    ? 'bg-blue-100' 
                    : 'bg-green-100'
                }`}>
                  {message.role === 'user' 
                    ? <User className="w-3 h-3 text-blue-600" />
                    : <Bot className="w-3 h-3 text-green-600" />
                  }
                </div>
                <div className={`max-w-[80%] rounded-xl px-3 py-2 ${
                  message.role === 'user'
                    ? 'bg-blue-600 text-white rounded-br-sm'
                    : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                }`}>
                  {message.role === 'user' ? (
                    <p className="text-xs whitespace-pre-wrap">{message.content}</p>
                  ) : (
                    <div className="text-xs whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: renderMarkdownToHTML(message.content) }} />
                  )}
                </div>
              </div>
            ))
          )}
          
          {isLoading && (
            <div className="flex gap-2">
              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                <Bot className="w-3 h-3 text-green-600" />
              </div>
              <div className="bg-gray-100 rounded-xl rounded-bl-sm px-3 py-2">
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t bg-white p-2 flex-shrink-0">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about clubs, tips..."
              className="flex-1 px-3 py-2 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              disabled={isLoading}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
              className="w-9 h-9 bg-green-600 text-white rounded-full flex items-center justify-center hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex-shrink-0"
            >
              {isLoading ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AskClubIQ;

